import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchFloorPlans } from "../../api/public.api.js";
import { imageOrPlaceholder, imgErrorFallback } from "../../lib/placeholders.js";
import { Container } from "../../components/layout/Container.jsx";
import { PageHero } from "../../components/shared/PageHero.jsx";
import { ImageWithSkeleton } from "../../components/shared/ImageWithSkeleton.jsx";
import { Lightbox } from "../../components/designs/Lightbox.jsx";
import { SEO } from "../../lib/seo.jsx";

/**
 * /floor-plans — a flat image gallery of floor plans. Same structure and
 * visual style as the flat Facades gallery: a responsive landscape grid of
 * images with titles, each opening in a lightbox.
 */
export default function FloorPlansPage() {
  const [lightbox, setLightbox] = useState({ images: [], index: -1 });

  const floorPlansQ = useQuery({
    queryKey: ["public", "floor-plans"],
    queryFn: fetchFloorPlans,
    staleTime: 5 * 60_000,
  });

  const floorPlans = floorPlansQ.data ?? [];

  const openLightboxByPlan = (plan) => {
    const idx = floorPlans.findIndex((p) => p._id === plan._id);
    setLightbox({
      images: floorPlans.map((p) =>
        imageOrPlaceholder(p.image, { pool: "design", key: p._id ?? p.slug ?? p.title })
      ),
      index: idx,
    });
  };

  return (
    <>
      <SEO
        title="Floor Plans"
        description="Browse our collection of home floor plans — find a layout that fits the way you live."
      />

      <PageHero
        eyebrow="Gallery"
        title="Floor Plans"
        description="Explore our range of floor plans — find a layout that fits the way you live."
      />

      <Container className="py-12 lg:py-16">
        {floorPlansQ.isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[4/3] animate-pulse rounded-xl bg-bg-elevated" />
            ))}
          </div>
        ) : floorPlans.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-border-subtle bg-bg-elevated px-6 py-16 text-center text-text-secondary">
            Floor plans coming soon.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {floorPlans.map((p) => {
              const key = p._id ?? p.slug ?? p.title;
              return (
                <button
                  key={p._id ?? p.slug}
                  type="button"
                  onClick={() => openLightboxByPlan(p)}
                  className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-border-subtle bg-bg-elevated"
                >
                  <ImageWithSkeleton
                    src={imageOrPlaceholder(p.image, { pool: "design", key })}
                    onError={imgErrorFallback(key)}
                    alt={p.title}
                    className="absolute inset-0 h-full w-full object-contain transition-transform duration-500 group-hover:scale-105"
                  />
                </button>
              );
            })}
          </div>
        )}
      </Container>

      {lightbox.index >= 0 && (
        <Lightbox
          images={lightbox.images}
          index={lightbox.index}
          onChange={(i) => setLightbox((s) => ({ ...s, index: i }))}
          onClose={() => setLightbox({ images: [], index: -1 })}
        />
      )}
    </>
  );
}
